import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon, ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { galleryPhotos } from '../data/gallery';
interface GalleryProps {
  /** Show only the first N photos (used for the homepage preview). */
  limit?: number;
}

/** Responsive photo grid with an enlargeable lightbox. */
export function Gallery({
  limit
}: GalleryProps) {
  const photos = limit ? galleryPhotos.slice(0, limit) : galleryPhotos;
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const close = () => setLightboxIndex(null);
  const step = (dir: 1 | -1) => setLightboxIndex(i => i === null ? i : (i + dir + photos.length) % photos.length);
  useEffect(() => {
    if (lightboxIndex === null) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
      if (e.key === 'ArrowRight') step(1);
      if (e.key === 'ArrowLeft') step(-1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lightboxIndex]);
  return <>
      <ul className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-6" data-id="element-181" data-mp="112mhebvq7wvh">
        {photos.map((photo, index) => <li key={photo.src} data-id="element-182" data-mp="153prehr3eyos" data-mpi={index}>
            <button type="button" onClick={() => setLightboxIndex(index)} className="block w-full rounded-xl overflow-hidden border border-zinc-800 hover:border-zinc-600 transition-colors duration-150 bg-zinc-950 focus:outline-none focus-visible:ring-2 focus-visible:ring-yellow-400" aria-label={`Enlarge photo: ${photo.alt}`} data-id="element-183" data-mp="152o6llk9yxtv">
              <img src={photo.src} alt={photo.alt} className="w-full h-auto object-contain" loading="lazy" decoding="async" data-id="element-184" data-mp="hghq0awsc9s1" />
            </button>
          </li>)}
      </ul>

      <AnimatePresence data-id="element-185" data-mp="1ssqudph5phmo">
        {lightboxIndex !== null && <motion.div role="dialog" aria-modal="true" aria-label="Photo viewer" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2
      }} className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center p-4" onClick={close} data-id="element-186" data-mp="1xsmzjs1lbf3t">
            <button type="button" onClick={close} className="absolute top-4 right-4 text-white/70 hover:text-white p-2 z-10" aria-label="Close photo viewer" data-id="element-187" data-mp="7nieud3sro5w">
              <XIcon className="w-8 h-8" data-id="element-188" data-mp="3b5a2jfawhlya" />
            </button>
            <button type="button" onClick={e => {
          e.stopPropagation();
          step(-1);
        }} className="absolute left-2 sm:left-4 text-white/70 hover:text-white p-2 z-10" aria-label="Previous photo" data-id="element-189" data-mp="3b5jsj2vy3t3z">
              <ChevronLeftIcon className="w-10 h-10" data-id="element-190" data-mp="3kwwl5wpkinb2" />
            </button>
            <button type="button" onClick={e => {
          e.stopPropagation();
          step(1);
        }} className="absolute right-2 sm:right-4 text-white/70 hover:text-white p-2 z-10" aria-label="Next photo" data-id="element-191" data-mp="3g2s94z1g3p34">
              <ChevronRightIcon className="w-10 h-10" data-id="element-192" data-mp="2ndle4a179hmm" />
            </button>
            <motion.img key={photos[lightboxIndex].src} initial={{
          opacity: 0,
          scale: 0.96
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.2,
          ease: [0.23, 1, 0.32, 1]
        }} src={photos[lightboxIndex].src} alt={photos[lightboxIndex].alt} className="max-w-full max-h-[85vh] object-contain rounded-lg" style={{
          maxWidth: '1200px'
        }} onClick={e => e.stopPropagation()} data-id="element-193" data-mp="12w3vv1rzfmv5" />
            <p className="absolute bottom-6 left-1/2 -translate-x-1/2 text-white/60 text-sm" data-id="element-194" data-mp="1qm6kt90locpj">
              {lightboxIndex + 1} / {photos.length}
            </p>
          </motion.div>}
      </AnimatePresence>
    </>;
}